import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Receipt, FileDown, CheckCircle2, Clock, XCircle, Ban, Landmark, Zap, History, Trash2, Send, MailCheck, ExternalLink, CreditCard, AlertTriangle } from "lucide-react";

const fmtEur = (v) => `€ ${Number(v || 0).toFixed(2)}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("it-IT") : "—";
const fmtDT = (s) => s ? new Date(s).toLocaleString("it-IT") : "—";

const STATO = {
  emessa: { l: "Emessa", c: "bg-blue-100 text-blue-800", i: Clock },
  pagata: { l: "Pagata", c: "bg-emerald-100 text-emerald-800", i: CheckCircle2 },
  scaduta: { l: "Scaduta", c: "bg-red-100 text-red-800", i: XCircle },
  annullata: { l: "Annullata", c: "bg-slate-200 text-slate-600", i: Ban },
};

const KIND_LABEL = {
  emissione: { l: "Emissione", c: "bg-blue-100 text-blue-800" },
  sollecito: { l: "Sollecito", c: "bg-amber-100 text-amber-800" },
  sospensione: { l: "Sospensione", c: "bg-red-100 text-red-800" },
  riattivazione: { l: "Riattivazione", c: "bg-emerald-100 text-emerald-800" },
  cancellazione: { l: "Cancellazione", c: "bg-slate-200 text-slate-700" },
};

/** Iter122bz · SuperAdmin · Fatture & Pagamenti (pro-forma). Iter122ca · auto cron. Iter122cc · filtro tenant + hard delete. */
export default function SuperAdminFatture() {
  const [items, setItems] = useState([]);
  const [aggr, setAggr] = useState(null);
  const [stato, setStato] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [refCRO, setRefCRO] = useState({});
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [running, setRunning] = useState(false);
  const [forecast, setForecast] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (stato) params.set("stato", stato);
    if (tenantId) params.set("tenant_id", tenantId);
    const qs = params.toString();
    return api.get(`/superadmin/fatture${qs ? `?${qs}` : ""}`)
      .then((r) => { setItems(r.data.items || []); setAggr(r.data.aggregato || null); })
      .catch(() => {});
  };
  const loadConfig = () => api.get("/superadmin/fatture/auto-config").then((r) => setConfig(r.data)).catch(() => {});
  const loadLogs = () => api.get("/superadmin/fatture/auto-log").then((r) => setLogs(r.data || [])).catch(() => {});
  const loadForecast = () => api.get("/superadmin/licenze-condominio/forecast").then((r) => setForecast(r.data)).catch(() => {});
  useEffect(() => { load(); }, [stato, tenantId]);
  useEffect(() => { loadConfig(); loadLogs(); loadForecast(); }, []);

  // Iter122cc · elenco tenant per filtro (da /superadmin/tenants, caricato una sola volta)
  const [tenantOptions, setTenantOptions] = useState([]);
  useEffect(() => {
    api.get("/superadmin/tenants").then((r) => {
      const opts = (r.data || []).map((t) => ({ id: t.id, name: t.nome || t.id }))
                                  .sort((a, b) => a.name.localeCompare(b.name));
      setTenantOptions(opts);
    }).catch(() => {});
  }, []);

  const runCron = async () => {
    if (!window.confirm("Eseguire ora il job di auto-emissione + auto-sospensione? Verranno emesse pro-forma per le licenze in scadenza e sospesi i tenant morosi.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/superadmin/fatture/auto-run");
      toast.success(`Cron eseguito · emissioni ${data.emissioni||0} · solleciti ${data.solleciti||0} · sospensioni ${data.sospensioni||0}`);
      load(); loadLogs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    setRunning(false);
  };

  const registraPagamento = async (f) => {
    const ref = refCRO[f.id] || window.prompt("CRO / riferimento pagamento:");
    if (!ref) return;
    setBusy(true);
    try {
      await api.post(`/superadmin/fatture/${f.id}/pagamento`, { metodo: "bonifico", riferimento: ref });
      toast.success("Pagamento registrato · pro-forma pagata"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    setBusy(false);
  };

  const annulla = async (f) => {
    if (!window.confirm(`Annullare la pro-forma ${f.numero}?`)) return;
    try { await api.delete(`/superadmin/fatture/${f.id}`); toast.success("Annullata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const cancella = async (f) => {
    // Iter122da · confermato dal nuovo dialog AlertDialog (non più window.confirm)
    setCancelBusy(true);
    try {
      await api.delete(`/superadmin/fatture/${f.id}/hard`);
      toast.success(`Pro-forma ${f.numero} cancellata`);
      setCancelDialog(null);
      load(); loadLogs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore");
    }
    setCancelBusy(false);
  };

  // Iter122da · dialog di conferma cancellazione (sostituisce window.confirm) — funziona anche su pro-forma già pagate
  const [cancelDialog, setCancelDialog] = useState(null); // fattura | null
  const [cancelBusy, setCancelBusy] = useState(false);

  const [inviaDialog, setInviaDialog] = useState(null); // {fattura, email, note, abilita_pagamento_online, busy} | null

  const openInviaDialog = (f) => {
    const defaultEmail = f.tenant_snapshot?.email || "";
    setInviaDialog({
      fattura: f,
      email: defaultEmail,
      note: "",
      abilita_pagamento_online: false,
      busy: false,
    });
  };

  const confermaInvia = async () => {
    if (!inviaDialog) return;
    const f = inviaDialog.fattura;
    setInviaDialog((s) => ({ ...s, busy: true }));
    try {
      const { data } = await api.post(`/superadmin/fatture/${f.id}/invia`, {
        email: (inviaDialog.email || "").trim(),
        note: (inviaDialog.note || "").trim(),
        abilita_pagamento_online: !!inviaDialog.abilita_pagamento_online,
      });
      const extra = data.stripe_url ? " (link Stripe generato)" : "";
      toast.success(`Pro-forma ${f.numero} trasmessa a ${data.trasmessa_a}${extra}`);
      setInviaDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore invio");
      setInviaDialog((s) => ({ ...s, busy: false }));
    }
  };

  const scaricaPdf = async (f) => {
    try {
      const res = await api.get(`/superadmin/fatture/${f.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${f.numero}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore scaricamento PDF"); }
  };

  return (
    <div className="space-y-4" data-testid="superadmin-fatture">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-brand" />
          <h3 className="font-display text-lg font-bold text-slate-800">Fatture &amp; Pagamenti</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={async () => {
            try {
              const res = await api.get("/superadmin/fatture/export-csv", { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
              const a = document.createElement("a"); a.href = url; a.download = `fatture_ginius_${new Date().toISOString().slice(0,10)}.csv`;
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
            } catch { toast.error("Errore export"); }
          }} className="h-9" data-testid="fatture-export-csv">
            <FileDown className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          <Button size="sm" variant="outline" onClick={runCron} disabled={running} data-testid="fatture-run-cron" className="h-9">
            <Zap className="w-4 h-4 mr-1" /> {running ? "In corso…" : "Esegui cron ora"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setShowLog((v) => !v); loadLogs(); }} data-testid="fatture-toggle-log" className="h-9">
            <History className="w-4 h-4 mr-1" /> {showLog ? "Nascondi log" : "Storico automazioni"}
          </Button>
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                  className="h-9 border-2 border-slate-200 rounded-md px-3 text-sm max-w-[220px]" data-testid="fatture-filter-tenant">
            <option value="">Tutti gli studi</option>
            {tenantOptions.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
          </select>
          <select value={stato} onChange={(e) => setStato(e.target.value)}
                  className="h-9 border-2 border-slate-200 rounded-md px-3 text-sm" data-testid="fatture-filter">
            <option value="">Tutti gli stati</option>
            <option value="emessa">Emesse</option>
            <option value="pagata">Pagate</option>
            <option value="scaduta">Scadute</option>
            <option value="annullata">Annullate</option>
          </select>
        </div>
      </div>

      {config && (
        <Card className="p-3 bg-slate-50 border-2 border-slate-200 text-xs text-slate-600" data-testid="fatture-auto-config">
          <b>Automazione attiva</b> · Emissione automatica <b>{config.lead_days} giorni</b> prima della scadenza licenza ·
          Sollecito email a <b>{config.reminder_before_days} giorni</b> dalla sospensione ·
          Auto-sospensione dopo <b>{config.grace_days} giorni</b> di ritardo (registrando il pagamento lo studio viene riattivato).
        </Card>
      )}

      {showLog && (
        <Card className="p-3 border-2" data-testid="fatture-auto-log">
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Storico automazioni · ultime {logs.length}</div>
          {logs.length === 0 && <div className="text-xs text-slate-400">Nessuna azione automatica registrata.</div>}
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {logs.slice(0, 100).map((l) => {
              const K = KIND_LABEL[l.kind] || { l: l.kind, c: "bg-slate-100" };
              return (
                <div key={l.id} className="flex flex-wrap items-center gap-2 text-xs border-b border-slate-100 pb-1.5">
                  <span className={`px-2 py-0.5 rounded-full font-semibold ${K.c}`}>{K.l}</span>
                  <span className="text-slate-700">{l.tenant_id}</span>
                  {l.numero && <span className="text-slate-500">{l.numero}</span>}
                  {"email_ok" in l && <span className={l.email_ok ? "text-emerald-600" : "text-red-600"}>email {l.email_ok ? "OK" : "KO"}</span>}
                  <span className="text-slate-400 ml-auto">{fmtDT(l.at)}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {aggr && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="p-3 bg-slate-50 border-2"><div className="text-xs text-slate-500 font-semibold uppercase">Documenti</div><div className="text-2xl font-bold">{aggr.totale}</div></Card>
          <Card className="p-3 bg-blue-50 border-2 border-blue-200"><div className="text-xs text-blue-600 font-semibold uppercase">Emesse</div><div className="text-lg font-bold text-blue-800">{fmtEur(aggr.emessa_eur)}</div></Card>
          <Card className="p-3 bg-emerald-50 border-2 border-emerald-200"><div className="text-xs text-emerald-600 font-semibold uppercase">Pagate</div><div className="text-lg font-bold text-emerald-800">{fmtEur(aggr.pagata_eur)}</div></Card>
          <Card className="p-3 bg-red-50 border-2 border-red-200"><div className="text-xs text-red-600 font-semibold uppercase">Scadute</div><div className="text-lg font-bold text-red-800">{fmtEur(aggr.scaduta_eur)}</div></Card>
        </div>
      )}

      {/* Iter122cn · Forecast ricavi da sub-licenze */}
      {forecast && forecast.sub_licenze_attive_totali > 0 && (
        <Card className="p-3 border-2 border-indigo-200 bg-indigo-50/40" data-testid="forecast-card">
          <div className="text-xs font-semibold uppercase text-indigo-800 mb-1.5">Forecast ricavi (sub-licenze condominio)</div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <div><b>{forecast.sub_licenze_attive_totali}</b> sub-licenze attive</div>
            <div>Imponibile annuo tot: <b className="text-indigo-800">{fmtEur(forecast.imponibile_annuo_totale)}</b></div>
            <div>Ricavo prox 90gg: <b className="text-indigo-800">{fmtEur(forecast.ricavo_forecast_90gg)}</b></div>
            <div className="text-xs text-slate-500">Top studi: {forecast.top_studi.slice(0,3).map((s) => `${s.tenant_id} (${fmtEur(s.imponibile)})`).join(" · ")}</div>
          </div>
        </Card>
      )}

      {items.length === 0 && <Card className="p-4 text-center text-slate-400 text-sm">Nessuna pro-forma. Emettine una da &quot;Piani &amp; Licenze&quot; → licenza attiva.</Card>}
      {items.map((f) => {
        const S = STATO[f.stato] || { l: f.stato, c: "bg-slate-100", i: Clock };
        const Ic = S.i;
        return (
          <Card key={f.id} className="p-3 sm:p-4 border-2" data-testid={`fattura-${f.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <b className="text-slate-800">{f.numero}</b>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${S.c}`}>
                    <Ic className="w-3 h-3" /> {S.l}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-1 break-words">
                  {f.tenant_snapshot?.ragione_sociale || f.tenant_id} · Emessa il {fmtDate(f.data_emissione)}
                </div>
                <div className="text-sm mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <span>Imp. <b>{fmtEur(f.imponibile)}</b></span>
                  {(f.cpa || 0) > 0 && <span className="text-slate-600">CPA {(f.cpa_pct||4)}% ({fmtEur(f.cpa)})</span>}
                  <span className="text-slate-600">IVA {(f.iva_pct||22)}% ({fmtEur(f.iva)})</span>
                  <span>Tot. doc. <b>{fmtEur(f.totale_documento || f.totale)}</b></span>
                  {(f.ritenuta || 0) > 0 && (
                    <span className="text-slate-600">RA {(f.ritenuta_pct||20)}% ({fmtEur(f.ritenuta)}, cod. {f.ritenuta_codice_tributo || "1040"})</span>
                  )}
                  {(f.netto_a_pagare != null) && (
                    <span>Netto <b className="text-brand">{fmtEur(f.netto_a_pagare)}</b></span>
                  )}
                </div>
                {f.trasmessa_at && (
                  <div className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-1 mt-1 inline-flex items-center gap-1"
                       data-testid={`fattura-trasmessa-${f.id}`}>
                    <MailCheck className="w-3 h-3" />
                    Trasmessa il {fmtDT(f.trasmessa_at)}{f.trasmessa_a ? ` a ${f.trasmessa_a}` : ""}
                  </div>
                )}
                {f.stripe_payment_url && f.stato !== "pagata" && (
                  <div className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-2 py-1 mt-1 inline-flex items-center gap-1"
                       data-testid={`fattura-stripe-${f.id}`}>
                    <CreditCard className="w-3 h-3" />
                    <span>Pagamento online abilitato</span>
                    <a href={f.stripe_payment_url} target="_blank" rel="noreferrer"
                       className="underline text-purple-700 hover:text-purple-900 ml-1 inline-flex items-center gap-0.5"
                       data-testid={`fattura-stripe-link-${f.id}`}>
                      apri link <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {f.stripe_paid_at && (
                  <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-1 inline-flex items-center gap-1"
                       data-testid={`fattura-stripe-pagata-${f.id}`}>
                    <CreditCard className="w-3 h-3" />
                    Pagata online (Stripe) il {fmtDT(f.stripe_paid_at)}
                  </div>
                )}
                {f.pagamento && (
                  <div className="text-xs text-emerald-700 mt-1">
                    Pagata il {fmtDate(f.pagamento.data)} · {f.pagamento.metodo}{f.pagamento.riferimento && ` · rif ${f.pagamento.riferimento}`}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {f.stato === "emessa" && (
                  <>
                    <Input placeholder="CRO" value={refCRO[f.id] || ""} onChange={(e) => setRefCRO((s) => ({ ...s, [f.id]: e.target.value }))}
                           className="h-9 w-32" data-testid={`fattura-cro-${f.id}`} />
                    <Button size="sm" disabled={busy} onClick={() => registraPagamento(f)} data-testid={`fattura-paga-${f.id}`} className="h-9 bg-brand hover:bg-brand-dark">
                      <Landmark className="w-4 h-4 mr-1" /> Registra pagamento
                    </Button>
                  </>
                )}
                {f.stato !== "annullata" && (
                  <Button size="sm" variant="outline" onClick={() => openInviaDialog(f)} data-testid={`fattura-invia-${f.id}`}
                          className="h-9 border-blue-300 text-blue-700 hover:bg-blue-50" title="Trasmetti la pro-forma al cliente via email">
                    <Send className="w-4 h-4 mr-1" /> {f.trasmessa_at ? "Reinvia" : "Invia al cliente"}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => scaricaPdf(f)} data-testid={`fattura-pdf-${f.id}`} className="h-9">
                  <FileDown className="w-4 h-4 mr-1" /> PDF
                </Button>
                {f.stato !== "pagata" && f.stato !== "annullata" && (
                  <Button size="sm" variant="outline" onClick={() => annulla(f)} data-testid={`fattura-annulla-${f.id}`}
                          className="h-9 text-amber-700 border-amber-300 hover:bg-amber-50" title="Annulla (mantiene la riga con stato annullata)">
                    <Ban className="w-4 h-4" />
                  </Button>
                )}
                {f.stato !== "annullata" && (
                  <Button size="sm" variant="outline" onClick={() => setCancelDialog(f)} data-testid={`fattura-cancella-${f.id}`}
                          className="h-9 text-red-600 border-red-300 hover:bg-red-50" title="Cancella definitivamente dal database (cestino)">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </Card>
        );
      })}

      {/* Iter122da · Dialog di conferma cancellazione (cestino) — attivo anche su pagate */}
      <AlertDialog open={!!cancelDialog} onOpenChange={(o) => { if (!o && !cancelBusy) setCancelDialog(null); }}>
        <AlertDialogContent data-testid="fattura-cancella-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Cancellare la pro-forma {cancelDialog?.numero}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <div>
                  Stai per <b className="text-red-700">rimuovere definitivamente</b> la pro-forma <b>{cancelDialog?.numero}</b> di{" "}
                  <b>{cancelDialog?.tenant_snapshot?.ragione_sociale || cancelDialog?.tenant_id}</b> dal database.
                </div>
                {cancelDialog?.stato === "pagata" && (
                  <div className="rounded-md border-2 border-amber-300 bg-amber-50 px-3 py-2 text-amber-900" data-testid="fattura-cancella-warn-pagata">
                    <b>Attenzione:</b> questa pro-forma risulta già <b>PAGATA</b>
                    {cancelDialog?.pagamento?.riferimento ? ` (rif. ${cancelDialog.pagamento.riferimento})` : ""}. Cancellandola perderai la traccia contabile del pagamento su Ginius.
                  </div>
                )}
                <div className="text-xs text-slate-500">Operazione non annullabile. L&apos;azione verrà registrata nello storico automazioni con il tuo account.</div>
                <div className="text-sm">Sei sicuro di voler cancellare?</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelBusy} data-testid="fattura-cancella-annulla">Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelBusy}
              onClick={(e) => { e.preventDefault(); if (cancelDialog) cancella(cancelDialog); }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="fattura-cancella-conferma">
              <Trash2 className="w-4 h-4 mr-1" />
              {cancelBusy ? "Cancellazione…" : "Sì, cancella"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter122cr · Dialog "Invia al cliente" con opzione pagamento online Stripe */}
      <Dialog open={!!inviaDialog} onOpenChange={(o) => { if (!o) setInviaDialog(null); }}>
        <DialogContent className="w-[95vw] max-w-lg p-4 sm:p-6" data-testid="fattura-invia-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">
              Trasmetti pro-forma {inviaDialog?.fattura?.numero}
            </DialogTitle>
          </DialogHeader>
          {inviaDialog && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="invia-email" className="text-xs font-semibold uppercase text-slate-500">Email destinatario</Label>
                <Input id="invia-email" type="email" value={inviaDialog.email}
                       onChange={(e) => setInviaDialog((s) => ({ ...s, email: e.target.value }))}
                       placeholder="lascia vuoto per usare la report_email dello studio"
                       data-testid="invia-email-input" />
              </div>
              <div>
                <Label htmlFor="invia-note" className="text-xs font-semibold uppercase text-slate-500">Note (opzionali)</Label>
                <Textarea id="invia-note" rows={3} value={inviaDialog.note}
                          onChange={(e) => setInviaDialog((s) => ({ ...s, note: e.target.value }))}
                          placeholder="Testo aggiunto in fondo al messaggio…"
                          data-testid="invia-note-input" />
              </div>
              <div className="rounded-lg border-2 border-purple-200 bg-purple-50/50 p-3">
                <div className="flex items-start gap-2">
                  <Checkbox id="invia-stripe" checked={inviaDialog.abilita_pagamento_online}
                            onCheckedChange={(v) => setInviaDialog((s) => ({ ...s, abilita_pagamento_online: !!v }))}
                            data-testid="invia-stripe-checkbox" className="mt-0.5" />
                  <div className="flex-1">
                    <Label htmlFor="invia-stripe" className="text-sm font-semibold text-purple-900 cursor-pointer">
                      Abilita pagamento online (Stripe)
                    </Label>
                    <p className="text-xs text-purple-800 mt-0.5">
                      Genera un link Stripe Checkout (carta di credito) e lo include nell&apos;email.
                      Alla ricezione del pagamento la pro-forma è marcata pagata
                      {inviaDialog.fattura?.modalita === "condominio" ? " e la sub-licenza del condominio viene rinnovata" : ""} automaticamente.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setInviaDialog(null)}
                    disabled={inviaDialog?.busy}
                    data-testid="invia-cancel">Annulla</Button>
            <Button onClick={confermaInvia}
                    disabled={inviaDialog?.busy}
                    className="bg-brand hover:bg-brand-dark"
                    data-testid="invia-conferma">
              <Send className="w-4 h-4 mr-1" />
              {inviaDialog?.busy ? "Invio in corso…" : (inviaDialog?.fattura?.trasmessa_at ? "Reinvia" : "Invia")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

